import * as React from "react";
import {
	VisualComponent,
	FocusableComponent,
	DataComponent,
	DataObserveKey,
	InlineText,
	useStaticMode,
	useUniqueId,
	cn,
} from "@siteimprove/fancylib";
import { FormControl } from "../form/form";
import { assignKnobName } from "../../../utils/documentation-utils";
import * as scss from "./input-field.scss";

export type InputFieldProps = FormControl<string> & {
	placeholder?: string;
	maxLength?: number;
	disabled?: boolean;
	type?: "email" | "password" | "search" | "number" | "date" | "color" | "text";
	inputMode?: "decimal" | "tel" | "numeric" | "email" | "url" | "search";
	autoComplete?: string;
	large?: boolean;
	fullWidth?: boolean;
	width?: number;
	autoFocus?: boolean;
	role?: string;
} & VisualComponent &
	FocusableComponent &
	DataComponent &
	DataObserveKey;

const internalInputField = (
	props: InputFieldProps,
	readOnly: boolean,
	ref?: React.Ref<HTMLInputElement>
) => {
	const {
		placeholder,
		value,
		onChange,
		onBlur,
		name,
		invalid,
		disabled,
		large,
		fullWidth,
		width,
		type,
		inputMode,
		autoComplete,
		className,
		style,
		autoFocus,
		role,
		...rest
	} = props;

	return (
		<input
			{...rest}
			data-component={props["data-component"] || "input-field"}
			data-observe-key={props["data-observe-key"]}
			type={!!type ? type : "text"}
			className={cn(
				scss.input,
				fullWidth && scss.fullWidth,
				large && scss.large,
				invalid && scss.invalid,
				className,
				"fancy-InputField"
			)}
			placeholder={placeholder}
			name={name}
			value={value}
			onChange={(e) => onChange(e.target.value)}
			onBlur={onBlur}
			disabled={disabled}
			readOnly={readOnly}
			inputMode={inputMode}
			aria-invalid={invalid}
			style={width ? { ...style, width: "100%", maxWidth: width } : style}
			ref={ref}
			autoComplete={autoComplete}
			autoFocus={autoFocus}
			role={role}
		/>
	);
};

export const InputField = React.forwardRef(
	(props: InputFieldProps, ref?: React.Ref<HTMLInputElement>) => {
		const isStatic = useStaticMode();

		if (isStatic) {
			if (!props.value) {
				return <></>;
			}

			return internalInputField({ ...props, disabled: true }, false, ref);
		}

		return internalInputField(props, false, ref);
	}
);

assignKnobName(InputField, "InputField");
InputField.displayName = "InputField";

export const ReadOnlyInputField = React.forwardRef(
	(props: Omit<InputFieldProps, "onChange">, ref?: React.Ref<HTMLInputElement>) =>
		internalInputField(
			{ ...props, onChange: () => {}, "data-component": "read-only-input-field" },
			true,
			ref
		)
);

export type InputFieldWithSlugProps = InputFieldProps &
	(
		| { leftSlug: React.ReactNode; rightSlug?: React.ReactNode }
		| { rightSlug: React.ReactNode; leftSlug?: React.ReactNode }
	);

export const InputFieldWithSlug = React.forwardRef(
	(props: InputFieldWithSlugProps, ref?: React.Ref<HTMLInputElement>) => {
		const {
			rightSlug,
			leftSlug,
			disabled,
			className,
			"aria-describedby": currentAriaDescribedby,
			...rest
		} = props;

		const inputId = useUniqueId("input");
		const leftSlugId = useUniqueId("left-slug");
		const rightSlugId = useUniqueId("right-slug");
		const isStatic = useStaticMode();

		if (isStatic && !props.value) {
			return <></>;
		}

		return (
			<div data-component="input-field-with-slug" className={cn(scss.slugGroup)}>
				{leftSlug &&
					slug({
						id: leftSlugId,
						disabled: disabled || isStatic,
						slug: leftSlug,
					})}
				<InputField
					{...rest}
					ref={ref}
					id={inputId}
					aria-describedby={cn(
						currentAriaDescribedby,
						!!leftSlug && leftSlugId,
						(!!leftSlug || !!rightSlug) && inputId,
						!!rightSlug && rightSlugId
					)}
					className={cn(className, scss.slugGroupItem)}
					disabled={disabled}
				/>
				{rightSlug &&
					slug({
						id: rightSlugId,
						disabled: disabled || isStatic,
						slug: rightSlug,
					})}
			</div>
		);
	}
);

type SlugProps = { id: string; disabled?: boolean; slug: React.ReactNode };

const slug = ({ id, disabled, slug }: SlugProps) => {
	const isPrimitive = typeof slug === "string" || typeof slug === "number";
	return (
		<div
			id={id}
			className={cn(
				scss.slug,
				scss.slugGroupItem,
				disabled && scss.disabled,
				isPrimitive && scss.primitive
			)}
		>
			{isPrimitive ? (
				<InlineText tone="subtle" lineHeight="single-line">
					{slug}
				</InlineText>
			) : (
				slug
			)}
		</div>
	);
};
